import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Home from './Home';
import Chat from './Chat';
import Diary from './Diary';
import Moments from './Moments';
import Memory from './Memory';
import Knowledge from './Knowledge';
import Worldbook from './Worldbook';
import English from './English';
import Letters from './Letters';
import ApiSettings from './ApiSettings';
import Profile from './Profile';
import SoulSeed from './SoulSeed';
import Tide from './Tide';
import Timeline from './Timeline';
import Thoughts from './Thoughts';
import KeyChain from './KeyChain';
import ToyBox from './ToyBox';
import Cabinet from './Cabinet';
import CoReading from './CoReading';
import BluetoothPage from './BluetoothPage';
import Pulse from './PulseView';
import Settings from './Settings';
import Ledger from './Ledger';
import GrudgeBook from './GrudgeBook';
import Lounge from './Lounge';
import Reminders from './Reminders';
import Paper from './Paper';
import { MusicProvider, MiniPlayer, MusicPage } from './MusicPlayer';
import FloatingChat from './FloatingChat';
import PushSetup from './PushSetup';
import VoiceCall from './VoiceCall';
import CallHistory from './CallHistory';
import Gallery from './Gallery';
import IncomingCall from './IncomingCall';
import { supabase } from './supabase';
import './App.css';

// API安全：全局拦截fetch，自动加认证头 [key-vault]
// 钥匙不进代码：存在本设备 localStorage，解锁后第一次用时让她粘一次；后端回 401 就清掉重新要。
window.aizhiKey = {
  // [key-mem] 内存里也留一份：iOS 的 localStorage 偶尔读出来是空的（后台/内存紧张那一下），那一次请求没带钥匙就被 401、钥匙又被清掉——她就老被踢去粘钥匙
  _mem: '',
  get: function() { var v = ''; try { v = localStorage.getItem('aizhi_api_key') || ''; } catch (e) {} if (v) { this._mem = v; return v; } return this._mem; },
  set: function(k) { k = String(k || '').trim(); this._mem = k; try { localStorage.setItem('aizhi_api_key', k); } catch (e) {} },
  clear: function() { this._mem = ''; try { localStorage.removeItem('aizhi_api_key'); } catch (e) {} }
};
(function() {
  if (window._aizhi_fetch_patched) return;
  window._aizhi_fetch_patched = true;
  var _fetch = window.fetch;
  window.fetch = function(url, opts) {
    opts = opts || {};
    var key = window.aizhiKey.get();
    if (!opts.headers) opts.headers = {};
    if (opts.headers instanceof Headers) {
      if (key && !opts.headers.has('X-Aizhi-Key')) opts.headers.set('X-Aizhi-Key', key);
    } else if (typeof opts.headers === 'object') {
      if (key && !opts.headers['X-Aizhi-Key'] && !opts.headers['x-aizhi-key']) opts.headers['X-Aizhi-Key'] = key;
    }
    var isApi = typeof url === 'string' && url.indexOf('/api/') === 0;
    return _fetch.call(window, url, opts).then(function(resp) {
      if (isApi && resp && resp.status === 401) {
        // [key-verify] 别一个 401 就把钥匙清了：先拿钥匙再敲一次门，真的不对才清；并记下是哪条请求被拒
        try { console.log('[key] 401 on ' + url); window.__aizhiLast401 = String(url); } catch (e) {}
        var k = window.aizhiKey.get();
        if (!k) { try { window.dispatchEvent(new CustomEvent('aizhi-key-invalid')); } catch (e) {} return resp; }
        _fetch.call(window, '/api/call/invite', { headers: { 'X-Aizhi-Key': k } }).then(function(r2) {
          if (r2.status === 401) {
            window.aizhiKey.clear();
            try { window.dispatchEvent(new CustomEvent('aizhi-key-invalid')); } catch (e) {}
          }
        }).catch(function() {});
      }
      return resp;
    });
  };
})();

// ========== 设备信标（presence传感层）==========
// 页面打开=launch，前后台切换=scene_change。后端digester据此判断"她来了"。
// 转后台也上报——"刚离开"也是活跃边界。节流15秒防iOS抖动。
(function() {
  if (window._aizhi_beacon_on) return;
  window._aizhi_beacon_on = true;
  var lastSent = 0;
  function report(trigger, appState, useBeacon) {
    var now = Date.now();
    if (now - lastSent < 15000 && trigger !== 'launch') return;
    lastSent = now;
    function send(extra) {
      var payload = Object.assign({ trigger: trigger, app_state: appState }, extra || {});
      if (useBeacon && navigator.sendBeacon) {
        // 后台瞬间页面可能被冻结，sendBeacon不能带header，鉴权走query
        navigator.sendBeacon('/api/device/state?aizhi_key=' + encodeURIComponent(window.aizhiKey.get()), new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      } else {
        fetch('/api/device/state', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).catch(function() {});
      }
    }
    // 电量能拿就带上（iOS Safari没有Battery API，拿不到就裸发，快捷指令补电量）
    if (navigator.getBattery) {
      navigator.getBattery().then(function(b) {
        send({ battery_pct: Math.round(b.level * 100), charging: b.charging });
      }).catch(function() { send(); });
    } else send();
  }
  report('launch', 'foreground');
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'visible') report('scene_change', 'foreground');
    else report('scene_change', 'background', true);
  });
})();

// ========== 真登录（Supabase Auth）==========
// 每台设备只需登录一次，session自动续期。PIN锁保留，作为登录之后的快捷屏锁。
function LoginScreen({ theme }) {
  var [email, setEmail] = useState('');
  var [password, setPassword] = useState('');
  var [busy, setBusy] = useState(false);
  var [err, setErr] = useState('');

  var doLogin = async function() {
    if (busy) return;
    var em = email.trim();
    if (!em || !password) { setErr('邮箱和密码都要填'); return; }
    setBusy(true); setErr('');
    try {
      var { error } = await supabase.auth.signInWithPassword({ email: em, password: password });
      if (error) {
        setErr(error.message === 'Invalid login credentials' ? '邮箱或密码不对' : ('登录失败: ' + error.message));
        setBusy(false);
      }
      // 成功时 onAuthStateChange 会自动放行，不需要手动跳转
    } catch(e) {
      setErr('网络异常: ' + e.message);
      setBusy(false);
    }
  };

  var inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '13px 16px', marginBottom: '12px',
    borderRadius: '12px',
    border: '1px solid ' + (theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'),
    background: theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.9)',
    color: theme === 'dark' ? '#E8E0D8' : '#3a3630',
    fontSize: '16px', outline: 'none'
  };

  return (
    <div className={'lock-screen ' + theme}>
      <div className="lock-content" style={{ width: '100%', maxWidth: '320px', padding: '0 28px' }}>
        <div className="lock-cat">{'\u{1F54A}\uFE0F'}</div>
        <div className="lock-title">aizhi</div>
        <div className="lock-subtitle">{'第一次见这台设备，先登录'}</div>
        <div style={{ marginTop: '24px' }}>
          <input style={inputStyle} type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" autoComplete="username" placeholder="邮箱"
            value={email} onChange={function(e) { setEmail(e.target.value); }}
            onKeyDown={function(e) { if (e.key === 'Enter') doLogin(); }} />
          <input style={inputStyle} type="password" autoComplete="current-password" placeholder="密码"
            value={password} onChange={function(e) { setPassword(e.target.value); }}
            onKeyDown={function(e) { if (e.key === 'Enter') doLogin(); }} />
          {err ? <div style={{ color: '#e57373', fontSize: '13px', marginBottom: '10px', textAlign: 'center' }}>{err}</div> : null}
          <button onClick={doLogin} disabled={busy} style={{
            width: '100%', padding: '13px', borderRadius: '12px', border: 'none',
            background: busy ? '#a89880' : '#8a7460', color: '#fff', fontSize: '16px', cursor: 'pointer'
          }}>{busy ? '登录中…' : '进门'}</button>
        </div>
        <div className="lock-hint" style={{ marginTop: '22px' }}>{'\u{1F43E}'}</div>
      </div>
    </div>
  );
}

function AuthGate({ theme, children }) {
  var [session, setSession] = useState(undefined); // undefined=检查中, null=未登录
  useEffect(function() {
    supabase.auth.getSession().then(function(r) { setSession((r.data && r.data.session) || null); });
    var sub = supabase.auth.onAuthStateChange(function(_event, s) { setSession(s); });
    return function() { try { sub.data.subscription.unsubscribe(); } catch(e) {} };
  }, []);
  if (session === undefined) {
    return <div className={'lock-screen ' + theme} />;
  }
  if (!session) return <LoginScreen theme={theme} />;
  return children;
}

function LockScreen({ onUnlock, theme }) {
  var [pin, setPin] = useState('');
  var [correctPin, setCorrectPin] = useState(null);
  var [shake, setShake] = useState(false);
  var [unlocking, setUnlocking] = useState(false);
  var [failedAttempts, setFailedAttempts] = useState(0);
  var [lockUntil, setLockUntil] = useState(null);
  var [lockCountdown, setLockCountdown] = useState('');
  var [pinLoadFailed, setPinLoadFailed] = useState(false);

  useEffect(function() {
    var cancelled = false;
    // 先等supabase恢复登录session，再去取PIN——防止冷启动时抢跑、以游客身份被RLS拦下
    function fetchPin(attempt) {
      supabase.from('settings').select('value').eq('key', 'lock_pin').single().then(function(r) {
        if (cancelled) return;
        if (r.data) {
          setCorrectPin(String(r.data.value).trim());
        } else if (r.error && r.error.code === 'PGRST116') {
          // 表里真的没有这一行，才允许用出厂密码兜底
          setCorrectPin('092500');
        } else if (attempt < 5) {
          // 网络/权限等临时故障：重试，绝不静默换答案
          setTimeout(function() { fetchPin(attempt + 1); }, 1200);
        } else {
          setPinLoadFailed(true);
        }
      });
    }
    supabase.auth.getSession().then(function() { fetchPin(0); }).catch(function() { fetchPin(0); });
    // 读取锁定状态
    var saved = localStorage.getItem('aizhi_lock_until');
    if (saved && Number(saved) > Date.now()) {
      setLockUntil(Number(saved));
    }
    var savedFails = localStorage.getItem('aizhi_failed_attempts');
    if (savedFails) setFailedAttempts(Number(savedFails));
    return function() { cancelled = true; };
  }, []);

  // 锁定倒计时
  useEffect(function() {
    if (!lockUntil) { setLockCountdown(''); return; }
    var timer = setInterval(function() {
      var remain = lockUntil - Date.now();
      if (remain <= 0) {
        setLockUntil(null);
        setFailedAttempts(0);
        localStorage.removeItem('aizhi_lock_until');
        localStorage.setItem('aizhi_failed_attempts', '0');
        setLockCountdown('');
        clearInterval(timer);
      } else {
        var min = Math.floor(remain / 60000);
        var sec = Math.floor((remain % 60000) / 1000);
        setLockCountdown(min + ':' + (sec < 10 ? '0' : '') + sec);
      }
    }, 1000);
    return function() { clearInterval(timer); };
  }, [lockUntil]);

  var handleDigit = function(d) {
    if (pin.length >= 6 || unlocking || lockUntil) return;
    var newPin = pin + d;
    setPin(newPin);
    if (newPin.length === 6) {
      if (correctPin === null) {
        // 钥匙还没从数据库取到，不判对错、不计失败次数
        setShake(true);
        setTimeout(function() { setPin(''); setShake(false); }, 500);
        return;
      }
      if (newPin === correctPin) {
        setUnlocking(true);
        setFailedAttempts(0);
        localStorage.setItem('aizhi_failed_attempts', '0');
        localStorage.removeItem('aizhi_lock_until');
        setTimeout(function() { onUnlock(); }, 400);
      } else {
        var newFails = failedAttempts + 1;
        setFailedAttempts(newFails);
        localStorage.setItem('aizhi_failed_attempts', String(newFails));
        if (newFails >= 5) {
          var until = Date.now() + 30 * 60 * 1000;
          setLockUntil(until);
          localStorage.setItem('aizhi_lock_until', String(until));
        }
        setShake(true);
        setTimeout(function() { setPin(''); setShake(false); }, 500);
      }
    }
  };

  var handleDelete = function() {
    if (unlocking || lockUntil) return;
    setPin(function(p) { return p.slice(0, -1); });
  };

  var dots = [0,1,2,3,4,5].map(function(i) {
    return <div key={i} className={'lock-dot' + (i < pin.length ? ' filled' : '') + (unlocking ? ' unlock' : '')} />;
  });

  var keys = [1,2,3,4,5,6,7,8,9,'',0,'del'].map(function(k) {
    if (k === '') return <div key="empty" className="lock-key empty" />;
    if (k === 'del') return (
      <button key="del" className="lock-key del" onClick={handleDelete}>
        <span>{'\u232B'}</span>
      </button>
    );
    return (
      <button key={k} className="lock-key" onClick={function() { handleDigit(String(k)); }}>
        <span>{k}</span>
      </button>
    );
  });

  return (
    <div className={'lock-screen ' + theme}>
      <div className="lock-content">
        <div className="lock-cat">{'\u{1F431}'}</div>
        <div className="lock-title">{'\u559C\uFF5E'}</div>
        {lockUntil ? (
          <div className="lock-subtitle" style={{color:'#e57373'}}>{'\u8BD5\u9519\u592A\u591A\u6B21\uFF0C\u8BF7\u7B49 ' + lockCountdown}</div>
        ) : pinLoadFailed ? (
          <div className="lock-subtitle" style={{color:'#e57373'}}>{'\u94A5\u5319\u6CA1\u53D6\u5230\uFF0C\u68C0\u67E5\u7F51\u7EDC\u540E\u91CD\u5F00'}</div>
        ) : correctPin === null ? (
          <div className="lock-subtitle" style={{opacity:0.6}}>{'\u6B63\u5728\u53D6\u94A5\u5319\u2026'}</div>
        ) : (
          <div className="lock-subtitle">{'\u8F93\u5165\u5BC6\u7801\u89C1\u54E5\u54E5'}</div>
        )}
        <div className={'lock-dots' + (shake ? ' shake' : '')}>
          {dots}
        </div>
        <div className="lock-keypad" style={lockUntil ? {opacity:0.35, pointerEvents:'none'} : undefined}>
          {keys}
        </div>
        <div className="lock-hint">{'\u{1F43E}'}</div>
      </div>
    </div>
  );
}

function Sidebar({ isOpen, onClose, theme }) {
  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}
      <div className={'sidebar' + (isOpen ? ' open' : '')}>
        <div className="sidebar-header">
          <div className="sidebar-title" style={{ color: theme === 'dark' ? '#E8E0D8' : undefined }}>{'\u66F4\u591A'}</div>
          <button className="sidebar-close" onClick={onClose}>&times;</button>
        </div>
        <div className="sidebar-menu">
          <NavLink to="/pulse" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg></span>
            <span className="sidebar-text">{'\u8109'}</span>
          </NavLink>
          <NavLink to="/toybox" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg></span>
            <span className="sidebar-text">{'\u73A9\u5177\u7BB1'}</span>
          </NavLink>
          <NavLink to="/cabinet" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg></span>
            <span className="sidebar-text">{'\u6536\u85CF\u67DC'}</span>
          </NavLink>
          <NavLink to="/memory" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg></span>
            <span className="sidebar-text">{'\u8BB0\u5FC6\u5E93'}</span>
          </NavLink>
          <NavLink to="/knowledge" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></span>
            <span className="sidebar-text">{'\u77E5\u8BC6\u5E93'}</span>
          </NavLink>
          <NavLink to="/worldbook" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></span>
            <span className="sidebar-text">{'\u4E16\u754C\u4E66'}</span>
          </NavLink>
          <NavLink to="/english" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/></svg></span>
            <span className="sidebar-text">{'\u82F1\u8BED\u89D2'}</span>
          </NavLink>
          <NavLink to="/ledger" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="14" x2="10" y2="14"/><line x1="6" y1="17" x2="8" y2="17"/></svg></span>
            <span className="sidebar-text">{'\u8BB0\u8D26'}</span>
          </NavLink>
          <NavLink to="/grudge" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 3h13a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4z"/><line x1="7" y1="3" x2="7" y2="21"/><line x1="11" y1="8" x2="15" y2="8"/><line x1="11" y1="12" x2="15" y2="12"/></svg></span>
            <span className="sidebar-text">{'\u5C0F\u672C\u672C'}</span>
          </NavLink>
          <NavLink to="/paper" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9h4"/><line x1="10" y1="7" x2="16" y2="7"/><line x1="10" y1="11" x2="16" y2="11"/><line x1="10" y1="15" x2="14" y2="15"/></svg></span>
            <span className="sidebar-text">{'\u4ED6\u7684\u62A5\u7EB8'}</span>
          </NavLink>
          <NavLink to="/letters" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></span>
            <span className="sidebar-text">{'\u4FE1\u7BB1'}</span>
          </NavLink>
          <NavLink to="/profile" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></span>
            <span className="sidebar-text">{'\u6027\u683C\u6863\u6848'}</span>
          </NavLink>
          <NavLink to="/timeline" className="sidebar-item" onClick={onClose}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>
            <span className="sidebar-text">{'\u65F6\u95F4\u8F74'}</span>
          </NavLink>
          <div className="sidebar-item" onClick={function() {
            onClose();
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeAction) {
              window.webkit.messageHandlers.nativeAction.postMessage('openRecovery');
            } else {
              window.open('/recovery', '_blank');
            }
          }} style={{ cursor: 'pointer', opacity: 0.5 }}>
            <span className="sidebar-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg></span>
            <span className="sidebar-text">Recovery</span>
          </div>
        </div>
      </div>
    </>
  );
}

// 路由持久化：仅iOS杀进程后reload恢复（60秒内），正常打开app回Home
function RouteRestore() {
  var navigate = useNavigate();
  var location = useLocation();
  useEffect(function() {
    if (location.pathname !== '/') {
      localStorage.setItem('aizhi_last_route', location.pathname);
      localStorage.setItem('aizhi_route_ts', String(Date.now()));
    }
  }, [location.pathname]);
  useEffect(function() {
    var last = localStorage.getItem('aizhi_last_route');
    var ts = parseInt(localStorage.getItem('aizhi_route_ts') || '0');
    var elapsed = Date.now() - ts;
    // 只在60秒内的reload恢复（iOS杀进程场景），超过60秒算正常打开
    if (last && last !== '/' && location.pathname === '/' && elapsed < 60000) {
      navigate(last, { replace: true });
    } else {
      localStorage.removeItem('aizhi_last_route');
    }
  }, []);
  return null;
}

// [call-mini] 通话挂在整个 app 上，不随页面卸掉：在 /call 时全屏，离开 /call 缩成右上角一个小窗，电话照常进行
function CallHost() {
  var location = useLocation();
  var navigate = useNavigate();
  var [active, setActive] = useState(false);
  var [invite, setInvite] = useState(null);
  var [tick, setTick] = useState(0);
  var onCall = location.pathname === '/call';
  useEffect(function() {
    if (onCall && !active) { setActive(true); setInvite((location.state && location.state.invite) || null); }
  }, [onCall]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(function() {
    if (!active || onCall) return;
    var iv = setInterval(function() { setTick(function(t) { return t + 1; }); }, 1000);
    return function() { clearInterval(iv); };
  }, [active, onCall]);
  if (!active) return null;
  var sec = Math.max(0, Math.floor((Date.now() - (window.__aizhiCallStart || Date.now())) / 1000));
  var mm = Math.floor(sec / 60), ss = sec % 60;
  var timeStr = (mm < 10 ? '0' : '') + mm + ':' + (ss < 10 ? '0' : '') + ss;
  var st = window.__aizhiCallStatus || 'idle';
  return (
    <>
      <style>{`
        .call-mini{position:fixed;top:calc(env(safe-area-inset-top) + 10px);right:12px;z-index:900;display:flex;align-items:center;gap:8px;height:36px;padding:0 14px 0 10px;border-radius:18px;border:1px solid rgba(184,102,61,.35);background:rgba(250,246,240,.92);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);color:#6b3a22;font-size:13px;font-variant-numeric:tabular-nums;letter-spacing:.06em;box-shadow:0 6px 18px rgba(43,34,28,.14);font-family:inherit}
        .app.dark .call-mini{background:rgba(30,26,24,.9);color:#f0d9c6;border-color:rgba(217,139,95,.45)}
        .call-mini i{width:8px;height:8px;border-radius:50%;background:#b8663d;animation:callMiniPulse 1.6s ease-in-out infinite}
        .call-mini[data-status=speaking] i{background:#d98b5f}
        .call-mini[data-status=listening] i{background:#c59b4f}
        @keyframes callMiniPulse{0%,100%{box-shadow:0 0 0 0 rgba(184,102,61,.45)}50%{box-shadow:0 0 0 6px rgba(184,102,61,0)}}
      `}</style>
      <div style={{ display: onCall ? 'block' : 'none', position: 'fixed', inset: 0, zIndex: 800 }}>
        <VoiceCall inviteOverride={invite}
          onEnd={function() { setActive(false); setInvite(null); }}
          onMinimize={function() { navigate(window.history.length > 2 ? -1 : '/chat'); }} />
      </div>
      {!onCall && (
        <button className="call-mini" data-status={st} onClick={function() { navigate('/call'); }} title="回到通话">
          <i />{'通话中 ' + timeStr}
        </button>
      )}
    </>
  );
}

// [key-vault] 解锁后第一次：让她把钥匙粘进来，存本设备
function KeyScreen({ theme, onDone }) {
  var [val, setVal] = useState('');
  var [busy, setBusy] = useState(false);
  var [err, setErr] = useState('');
  var submit = function() {
    var k = val.trim();
    if (!k || busy) return;
    setBusy(true); setErr('');
    fetch('/api/call/invite', { headers: { 'X-Aizhi-Key': k } }).then(function(r) {
      if (r.status === 401) { setErr('\u94a5\u5319\u4e0d\u5bf9'); setBusy(false); return; }
      window.aizhiKey.set(k);
      setBusy(false);
      onDone();
    }).catch(function() { setErr('\u8fde\u4e0d\u4e0a\u540e\u7aef\uff0c\u68c0\u67e5\u7f51\u7edc'); setBusy(false); });
  };
  var dark = theme === 'dark';
  return (
    <div className={'lock-screen ' + theme}>
      <div className="lock-content">
        <div className="lock-cat">{'\u{1F511}'}</div>
        <div className="lock-title">{'\u8fd9\u53f0\u8bbe\u5907\u8fd8\u6ca1\u94a5\u5319'}</div>
        <div className="lock-subtitle" style={err ? { color: '#e57373' } : undefined}>{err || '\u628a .env \u91cc\u7684 AIZHI_API_KEY \u7c98\u8fdb\u6765\uff0c\u53ea\u7528\u4e00\u6b21'}</div>
        <input type="password" value={val} autoFocus autoComplete="off" autoCapitalize="off" spellCheck={false}
          onChange={function(e) { setVal(e.target.value); }}
          onKeyDown={function(e) { if (e.key === 'Enter') submit(); }}
          style={{ marginTop: 18, width: 240, maxWidth: '80vw', padding: '10px 12px', borderRadius: 10, border: '1px solid ' + (dark ? '#333' : '#ccc'), background: dark ? '#1a1a22' : '#fff', color: dark ? '#eee' : '#222', fontSize: 15, outline: 'none', textAlign: 'center' }} />
        <button onClick={submit} disabled={busy || !val.trim()}
          style={{ marginTop: 14, padding: '9px 26px', borderRadius: 20, border: 'none', background: '#c4956a', color: '#fff', fontSize: 14, opacity: (busy || !val.trim()) ? 0.5 : 1, cursor: 'pointer' }}>
          {busy ? '\u2026' : '\u5b58\u8d77\u6765'}
        </button>
      </div>
    </div>
  );
}

function App() {
  var [sidebarOpen, setSidebarOpen] = useState(false);
  var [theme, setTheme] = useState('light');
  var [showAvatar, setShowAvatar] = useState(true);
  var [locked, setLocked] = useState(function() {
    return localStorage.getItem('aizhi_unlocked') !== 'true';
  });
  var [colorTheme, setColorTheme] = useState(localStorage.getItem('cal_theme') || '');
  var [incomingInvite, setIncomingInvite] = useState(null);
  var [keyReady, setKeyReady] = useState(function() { return !!window.aizhiKey.get(); });   // [key-vault]
  useEffect(function() {   // [key-vault] 后端说钥匙不对 → 回到要钥匙那一页
    var onInvalid = function() { setKeyReady(false); };
    window.addEventListener('aizhi-key-invalid', onInvalid);
    return function() { window.removeEventListener('aizhi-key-invalid', onInvalid); };
  }, []);

  // 来电轮询
  useEffect(function() {
    var pollInvite = setInterval(async function() {
      try {
        var resp = await fetch('/api/call/invite');
        var data = await resp.json();
        if (data.invite && data.invite.status === 'pending') {
          setIncomingInvite(data.invite);
        } else {
          setIncomingInvite(null);
        }
      } catch(e) {}
    }, 8000);
    return function() { clearInterval(pollInvite); };
  }, []);

  // 监听色板变化
  useEffect(function() {
    var poll = setInterval(function() {
      var t = localStorage.getItem('cal_theme') || '';
      setColorTheme(function(prev) { return prev !== t ? t : prev; });
    }, 300);
    return function() { clearInterval(poll); };
  }, []);

  // 向系统申报页面明暗——iOS键盘颜色跟这个走，不跟系统设置走
  useEffect(function() {
    var dark = theme === 'dark';
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
    var meta = document.querySelector('meta[name="color-scheme"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'color-scheme');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', dark ? 'dark' : 'light');
    // 状态栏也递条子：PWA全屏模式下iOS按theme-color刷状态栏底色
    var tc = document.querySelector('meta[name="theme-color"]');
    if (!tc) {
      tc = document.createElement('meta');
      tc.setAttribute('name', 'theme-color');
      document.head.appendChild(tc);
    }
    tc.setAttribute('content', dark ? '#111114' : '#F3F3F1');
    // 顺带把html/body底色染成同款，键盘弹起时不再露白底
    var bg = dark ? '#111114' : '#F3F3F1';
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
  }, [theme]);

  var toggleTheme = function() {
    setTheme(function(t) { return t === 'dark' ? 'light' : 'dark'; });
  };

  var handleUnlock = function() {
    setLocked(false);
    localStorage.setItem('aizhi_unlocked', 'true');
  };

  var handleLock = function() {
    setLocked(true);
    localStorage.removeItem('aizhi_unlocked');
  };

  if (locked) {
    return (
      <AuthGate theme={theme}>
        <LockScreen onUnlock={handleUnlock} theme={theme} />
      </AuthGate>
    );
  }

  if (!keyReady) {   // [key-vault]
    return (
      <AuthGate theme={theme}>
        <KeyScreen theme={theme} onDone={function() { setKeyReady(true); }} />
      </AuthGate>
    );
  }

  return (
    <AuthGate theme={theme}>
    <MusicProvider apiBase="">
    <BrowserRouter>
      <RouteRestore />
      <div className={'app ' + theme + (colorTheme && colorTheme !== 'default' ? ' theme-' + colorTheme : '')}>
        <PushSetup />
        <Sidebar isOpen={sidebarOpen} onClose={function() { setSidebarOpen(false); }} theme={theme} />
        <div className="app-content">
          <Routes>
            <Route path="/" element={<Home onMenuClick={function() { setSidebarOpen(true); }} />} />
            <Route path="/chat" element={<Chat showAvatar={showAvatar} />} />
            <Route path="/moments" element={<Moments />} />
            <Route path="/diary" element={<Diary />} />
            <Route path="/memory" element={<Memory />} />
            <Route path="/knowledge" element={<Knowledge />} />
            <Route path="/worldbook" element={<Worldbook />} />
            <Route path="/english" element={<English />} />
            <Route path="/letters" element={<Letters />} />
            <Route path="/api-settings" element={<ApiSettings />} />
            <Route path="/profile" element={<Profile onMenuClick={function() { setSidebarOpen(true); }} />} />
            <Route path="/soul-seed" element={<SoulSeed />} />
            <Route path="/tide" element={<Tide />} />
            <Route path="/timeline" element={<Timeline />} />
            <Route path="/thoughts" element={<Thoughts />} />
            <Route path="/toybox" element={<ToyBox theme={theme} />} />
            <Route path="/cabinet" element={<Cabinet />} />
            <Route path="/keychain" element={<KeyChain onMenuClick={function() { setSidebarOpen(true); }} />} />
            <Route path="/reading" element={<CoReading />} />
            <Route path="/ledger" element={<Ledger />} />
            <Route path="/grudge" element={<GrudgeBook />} />
            <Route path="/lounge" element={<Lounge />} />
            <Route path="/reminders" element={<Reminders />} />
            <Route path="/paper" element={<Paper />} />
            <Route path="/bluetooth" element={<BluetoothPage theme={theme} />} />
            <Route path="/pulse" element={<Pulse />} />
            <Route path="/call" element={<div />} />   {/* [call-mini] 真正的通话页由 CallHost 挂着 */}
            <Route path="/call-history" element={<CallHistory />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/music" element={<MusicPage />} />
            <Route path="/settings" element={
              <Settings
                theme={theme}
                onToggleTheme={toggleTheme}
                showAvatar={showAvatar}
                onToggleAvatar={function() { setShowAvatar(!showAvatar); }}
                onLock={handleLock}
              />
            } />
          </Routes>
        </div>
        <MiniPlayer />
        <FloatingChat />
        <nav className="bottom-nav">
          <NavLink to="/" className={function(p) { return 'nav-item' + (p.isActive ? ' active' : ''); }} end>
            <div className="nav-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>
            <div className="nav-label">{'\u4E3B\u9875'}</div>
          </NavLink>
          <NavLink to="/moments" className={function(p) { return 'nav-item' + (p.isActive ? ' active' : ''); }}>
            <div className="nav-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg></div>
            <div className="nav-label">{'\u670B\u53CB\u5708'}</div>
          </NavLink>
          <NavLink to="/chat" className={function(p) { return 'nav-item' + (p.isActive ? ' active' : ''); }}>
            <div className="nav-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
            <div className="nav-label">{'\u804A\u5929'}</div>
          </NavLink>
          <NavLink to="/diary" className={function(p) { return 'nav-item' + (p.isActive ? ' active' : ''); }}>
            <div className="nav-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg></div>
            <div className="nav-label">{'\u65E5\u8BB0'}</div>
          </NavLink>
          <NavLink to="/settings" className={function(p) { return 'nav-item' + (p.isActive ? ' active' : ''); }}>
            <div className="nav-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg></div>
            <div className="nav-label">{'\u8BBE\u7F6E'}</div>
          </NavLink>
        </nav>
      </div>
      <CallHost />   {/* [call-mini] */}
      {incomingInvite && <IncomingCall invite={incomingInvite} onDismiss={function() { setIncomingInvite(null); }} />}
    </BrowserRouter>
    </MusicProvider>
    </AuthGate>
  );
}

export default App;
